// ui/pages/home/index.tsx
import { type ReactElement, memo, useMemo } from "react";

import { Authenticated } from "@/components/Authenticated";
import SEOHead from "@/components/SEOHead";
import HomeProvisioningBanner from "@/components/pages/home/HomeProvisioningBanner";
import HomeSkeleton from "@/components/pages/home/HomeSkeleton";
import { ROLES } from "@/configs";
import { useDashboard } from "@/contexts/DashboardContext";
import { useSignupProvisioningGate } from "@/hooks/useSignupProvisioning";
import SidebarLayout from "@/layouts/SidebarLayout";
import { useSelector } from "@/store";
import { rootReducerName } from "@/store/rootReducer";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import dynamic from "next/dynamic";

const I18N_FEATURE_NS = "features/dashboard";
const I18N_COMMON_NS = "common";

// Dynamic import keeps the larger dashboard shell out of the initial home chunk.
const RoleBasedDashboard = dynamic(() => import("@/components/features/dashboard/RoleBasedDashboard"), {
  loading: () => <HomeSkeleton />,
  ssr: false
});

const HomePage = memo(() => {
  const { t } = useTranslation([I18N_FEATURE_NS, I18N_COMMON_NS]);
  const { role, loading } = useDashboard();
  const user = useSelector((state) => state[rootReducerName.auth].user);
  const authLoading = useSelector((state) => state[rootReducerName.auth].loading);
  const {
    checking: signupSetupChecking,
    content: signupProvisioningContent,
    requiresSetup
  } = useSignupProvisioningGate();

  const isManager = useMemo(() => {
    return role?.some((r: { name: string }) => r.name === ROLES.MANAGER) ?? false;
  }, [role]);

  const isLoading = useMemo(() => {
    return authLoading || (!authLoading && user && loading);
  }, [authLoading, user, loading]);

  if (isLoading || signupSetupChecking || requiresSetup) {
    return <HomeSkeleton />;
  }

  return (
    <>
      <SEOHead
        title={t("Dashboard")}
        description={t("View your sales performance metrics and team activity")}
        noindex={true}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Worksine",
          applicationCategory: "BusinessApplication",
          applicationSubCategory: "CRM Software",
          operatingSystem: "Web Browser",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD"
          },
          featureList: [
            t("AI-powered sales automation"),
            t("Pipeline management"),
            t("Activity tracking"),
            t("Sales coaching"),
            t("CRM integration"),
            t("Real-time analytics")
          ]
        }}
      />

      <HomeProvisioningBanner content={signupProvisioningContent} />
      <RoleBasedDashboard showSelect={isManager} />
    </>
  );
});

// Add display name for React DevTools
HomePage.displayName = "HomePage";

// Type-safe getLayout property with authentication wrapper
(HomePage as any).getLayout = (page: ReactElement) => (
  <Authenticated authRequire>
    <SidebarLayout chatBoxOption="popup">{page}</SidebarLayout>
  </Authenticated>
);

export const getStaticProps = async ({ locale }: { locale?: string }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? "en", [I18N_COMMON_NS, I18N_FEATURE_NS]))
  }
});

export default HomePage;
